/**
 * @fileoverview The page a buyer lands on after paying — the last thing they
 * see of a purchase, and until now the least accurate.
 *
 * Three things were wrong with it, and they compounded.
 *
 * **It congratulated everyone on the same plan.** The heading was the literal
 * "Welcome to Pro!" — and `pro` is the LEGACY tier nobody can buy, so an
 * instructor who had just paid $39 for Studio AI was welcomed to a plan that
 * does not exist. This is the exact failure `lib/tiers` was built to end; the
 * guard missed it because it greps for the `concertmaster === … ? … : …`
 * ternary shape and a bare string literal is not that shape. The tier now
 * comes from the SESSION, resolved server-side from the price that was
 * actually charged, and is rendered through `tierDisplayName`.
 *
 * **The $9 minute top-up landed here too**, sharing one `return_url`, so
 * buying minutes told you that you had subscribed. `kind` on the session
 * metadata is what separates them, and the two purchases now confirm
 * themselves in their own words and go back to different places: a plan is an
 * account-level change and belongs on Settings, a pack of minutes is something
 * you bought mid-task and belongs back where you were.
 *
 * **It polled forever.** A two-second retry with no cap and no handling for an
 * expired session left "Processing your payment…" spinning for as long as the
 * tab stayed open. It now gives up, and says what to do next.
 *
 * The auto-redirect is gone with it. Three seconds is not long enough to read
 * a confirmation, and being moved off the page while reading it is how someone
 * ends up unsure whether they were charged.
 */

import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { supportEmail } from "@/lib/companyBrand";
import { tierDisplayName } from "@/lib/tiers";

/**
 * How long to keep asking Stripe whether the payment landed.
 *
 * A card is settled by the time the buyer gets here, so this is really about
 * the seconds our own webhook takes to record it. Sixty seconds of two-second
 * polls is generous for that and short enough that a session which will never
 * complete stops pretending it might.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

const Frame = ({ children }) => (
  <div className="studio-spotlight flex min-h-screen items-center justify-center p-8">
    <div className="max-w-md text-center" data-testid="checkout-return">{children}</div>
  </div>
);

/**
 * Body copy here is `--text-secondary`, NOT the `--text-tertiary` this page
 * used to use for it. Tertiary is the CAPTION token — 3.45:1 on the spotlight
 * ground, under the 4.5:1 AA floor for 14px — and the sentence it was carrying
 * is the one that tells somebody whether they were charged. Secondary clears
 * it at 6.99:1. Caught by `scripts/theme-audit.mjs`, which is the only thing
 * that would have.
 */
const Heading = ({ children }) => (
  <h1 className="mb-2 font-serif text-2xl font-light text-[var(--text-primary)]">{children}</h1>
);

const Action = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="mt-6 rounded-full bg-[var(--accent-yellow)] px-6 py-2 text-sm font-medium text-[var(--text-inverse)] transition-transform active:scale-95"
  >
    {children}
  </button>
);

const CheckoutReturnPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [status, setStatus] = useState("loading");
  const [purchase, setPurchase] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }

    let cancelled = false;
    let polls = 0;

    const check = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/subscription/stripe/session-status?session_id=${sessionId}`);
        if (cancelled) return;

        if (res.data.status === "complete") {
          setPurchase(res.data);
          // Pick up the new tier or minute balance before showing anything, so
          // whatever the buyer navigates to next is already correct.
          try {
            const me = await api.get("/auth/me");
            if (!cancelled) setUser(me.data);
          } catch {
            // The purchase succeeded either way — a stale profile is a refresh
            // away, and reporting it as a failed payment would be a lie.
          }
          if (!cancelled) setStatus("success");
          return;
        }

        // An expired session is terminal: Stripe will not complete it, so
        // polling on is just a spinner that never resolves.
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }

        polls += 1;
        if (polls >= MAX_POLLS) {
          setStatus("slow");
          return;
        }
        setStatus("processing");
        timerRef.current = setTimeout(check, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    check();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchParams, setUser]);

  const goSettings = useCallback(() => navigate("/settings", { replace: true }), [navigate]);
  const goBack = useCallback(() => navigate("/dashboard", { replace: true }), [navigate]);

  if (status === "loading" || status === "processing") {
    return (
      <Frame>
        <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
        <Heading>Confirming your payment…</Heading>
        <p className="text-sm text-[var(--text-secondary)]">This should only take a moment.</p>
      </Frame>
    );
  }

  if (status === "success") {
    const isTopUp = purchase?.kind === "topup";

    if (isTopUp) {
      const minutes = purchase?.topup_minutes;
      return (
        <Frame>
          <Zap className="mx-auto mb-4 h-12 w-12 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
          <Heading>{minutes ? `${minutes} minutes added` : "Minutes added"}</Heading>
          <p className="text-sm text-[var(--text-secondary)]">
            They never expire, and your monthly minutes are always used first.
          </p>
          {/* Back to the app, not to Settings: a top-up is bought mid-task —
              usually from the recording screen, having just run out — so the
              useful destination is the work, not the account page. */}
          <Action onClick={goBack}>Back to Hans</Action>
        </Frame>
      );
    }

    // Named from the price that was charged. Absent or unrecognised falls back
    // to a plain thank-you rather than guessing a plan.
    const tier = purchase?.tier;
    return (
      <Frame>
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
        <Heading>{tier ? `You're on ${tierDisplayName(tier)}` : "You're all set"}</Heading>
        <p className="text-sm text-[var(--text-secondary)]">
          Your subscription is active. A receipt is on its way to your email.
        </p>
        <Action onClick={goSettings}>Go to Settings</Action>
      </Frame>
    );
  }

  if (status === "slow" || status === "expired") {
    // Deliberately NOT an error: an expired session means nobody was charged,
    // and a slow one usually means our webhook is a few seconds behind. Both
    // are honest as "not yet", and saying "something went wrong" about a
    // payment that may have succeeded is the worst thing this page could do.
    return (
      <Frame>
        <Loader2 className="mx-auto mb-4 h-12 w-12 text-[var(--text-tertiary)]" strokeWidth={1.5} />
        <Heading>{status === "expired" ? "This checkout expired" : "Still confirming"}</Heading>
        <p className="text-sm text-[var(--text-secondary)]">
          {status === "expired"
            ? "Nothing was charged. You can start again whenever you're ready."
            : "Your payment may still be going through. Check Settings in a minute — if it hasn't appeared, nothing was charged twice."}
        </p>
        <Action onClick={goSettings}>Go to Settings</Action>
      </Frame>
    );
  }

  return (
    <Frame>
      <XCircle className="mx-auto mb-4 h-12 w-12 text-[var(--state-danger)]" strokeWidth={1.5} />
      <Heading>We couldn&rsquo;t confirm your payment</Heading>
      <p className="text-sm text-[var(--text-secondary)]">
        Check Settings to see whether it went through. If anything looks wrong, email{" "}
        <a href={`mailto:${supportEmail()}`} className="text-[var(--accent-yellow-text)] underline underline-offset-2">
          {supportEmail()}
        </a>{" "}
        and we&rsquo;ll sort it out.
      </p>
      <Action onClick={goSettings}>Go to Settings</Action>
    </Frame>
  );
};

export default CheckoutReturnPage;
